import { useEffect, useId, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../../../Utils/api';
import { formatShortDate } from '../../../Utils/dates/formatDate';
import { AdminSurface, Button, EmptyState, ErrorState, Field, LoadingState, PageHeader, Panel, StatusPill } from '../../../Components/AdminSurface';
import styles from './FeedbackInspection.module.css';

const STATUSES = ['Open', 'Acknowledged', 'Resolved'];
const TONES = { Open: 'warn', Acknowledged: 'neutral', Resolved: 'good' };

// Legacy records may lack a date; new Date(null) is 1970 and bad strings are NaN, so both are guarded.
const formatDate = value => {
    if (value == null || value === '' || Number.isNaN(new Date(value).getTime())) return 'Date not recorded';
    return `${formatShortDate(value)} ${new Date(value).getFullYear()}`;
};

const Feedback = ({ data: feedback }) => {
    const [saved, setSaved] = useState(feedback.status);
    const [value, setValue] = useState(feedback.status);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const headingId = useId();

    const onUpdate = async () => {
        setBusy(true);
        setMessage('');
        try {
            await API.updateFeedback({ feedbackId: feedback._id, status: value });
            setSaved(value);
            setMessage('Status updated.');
            toast.success('Feedback State Updated', { position: 'bottom-center' });
        } catch (error) {
            setMessage('Failed to update status.');
            toast.error('Failed to Update Feedback Status', { position: 'bottom-center' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <li className={styles.item}>
            <article aria-labelledby={headingId} className={styles.card}>
                <div className={styles.top}>
                    <h3 id={headingId} className={styles.subject}>
                        {feedback.subject}
                    </h3>
                    <StatusPill tone={TONES[saved] || 'neutral'}>{saved}</StatusPill>
                </div>
                <p className={styles.meta}>
                    {feedback.type} · {formatDate(feedback.date)} · {feedback.name} ({feedback.email})
                </p>
                <p className={styles.message}>{feedback.message}</p>
                <div className={styles.actions}>
                    <div className={styles.statusField}>
                        <Field as="select" label="Status" value={value} onChange={e => setValue(e.target.value)}>
                            {STATUSES.map(s => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Field>
                    </div>
                    <Button variant="primary" busy={busy} disabled={value === saved} onClick={onUpdate}>
                        Update
                    </Button>
                    <span role="status" aria-live="polite" className={styles.note}>
                        {message}
                    </span>
                </div>
            </article>
        </li>
    );
};

export default function FeedbackInspection() {
    const [feedbacks, setFeedbacks] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [filter, setFilter] = useState('All');

    const fetchAndSetFeedbacks = async () => {
        setLoadError(false);
        try {
            const { data } = await API.getFeedbacks();
            setFeedbacks(data);
        } catch (error) {
            setLoadError(true);
        }
    };

    useEffect(() => {
        fetchAndSetFeedbacks();
    }, []);

    let body;
    let sub;
    if (loadError) body = <ErrorState label="feedback" onRetry={fetchAndSetFeedbacks} />;
    else if (feedbacks === null) body = <LoadingState label="feedback" />;
    else {
        const count = s => feedbacks.filter(f => f.status === s).length;
        const shown = filter === 'All' ? feedbacks : feedbacks.filter(f => f.status === filter);
        sub = `${feedbacks.length} ${feedbacks.length === 1 ? 'item' : 'items'} · ${count('Open')} open`;
        body = feedbacks.length === 0 ? (
            <EmptyState>No feedback yet.</EmptyState>
        ) : (
            <>
                <div role="group" aria-label="Filter by status" className={styles.chips}>
                    {['All', ...STATUSES].map(s => (
                        <Button key={s} aria-pressed={filter === s} onClick={() => setFilter(s)}>
                            {`${s} (${s === 'All' ? feedbacks.length : count(s)})`}
                        </Button>
                    ))}
                </div>
                {shown.length === 0 ? (
                    <EmptyState>No feedback with this status.</EmptyState>
                ) : (
                    <ul className={styles.list}>
                        {shown.map(fb => (
                            <Feedback key={fb._id} data={fb} />
                        ))}
                    </ul>
                )}
            </>
        );
    }

    return (
        <AdminSurface>
            <PageHeader title="Feedback" sub={sub} />
            <Panel title="Submissions">{body}</Panel>
        </AdminSurface>
    );
}
